/**
 * Generated by orval v7.21.0 🍺
 * Do not edit manually.
 * Mission Impact Auth API
 * OpenAPI spec version: v1
 */
import { useQuery } from '@tanstack/react-query';
import type {
  DataTag,
  DefinedInitialDataOptions,
  DefinedUseQueryResult,
  QueryClient,
  QueryFunction,
  QueryKey,
  UndefinedInitialDataOptions,
  UseQueryOptions,
  UseQueryResult,
} from '@tanstack/react-query';

import type {
  GetOrganizationProductEntitlementsParams,
  GetUserProductEntitlementsParams,
  ProblemDetails,
  ProductEntitlementsRollup,
} from '../missionImpactAuthAPI.schemas';

import { instance } from '../../mutator-auth';
import type { ErrorType } from '../../mutator-auth';

type SecondParameter<T extends (...args: never) => unknown> = Parameters<T>[1];

/**
 * Gets the rolled-up entitlements for the authenticated user's product registration. Returns detailed information including: effective count (sum of cumulative counts or max of minimum counts), trial status, expiration dates, and individual entitlement breakdown. For service-to-service authentication with 'Entitlements.Read' role, include 'userId' query parameter. Optionally include a signed JWT with entitlement claims by setting 'jwt=true'.
 * @summary Get user product entitlements
 */
export const getUserProductEntitlements = (
  productId: string,
  params?: GetUserProductEntitlementsParams,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<ProductEntitlementsRollup>(
    { url: `/v1/products/${productId}/entitlements`, method: 'GET', params, signal },
    options
  );
};

export const getGetUserProductEntitlementsQueryKey = (
  productId?: string,
  params?: GetUserProductEntitlementsParams
) => {
  return [`/v1/products/${productId}/entitlements`, ...(params ? [params] : [])] as const;
};

export const getGetUserProductEntitlementsQueryOptions = <
  TData = Awaited<ReturnType<typeof getUserProductEntitlements>>,
  TError = ErrorType<ProblemDetails>,
>(
  productId: string,
  params?: GetUserProductEntitlementsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserProductEntitlements>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ?? getGetUserProductEntitlementsQueryKey(productId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getUserProductEntitlements>>> = ({
    signal,
  }) => getUserProductEntitlements(productId, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!productId, ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getUserProductEntitlements>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetUserProductEntitlementsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getUserProductEntitlements>>
>;
export type GetUserProductEntitlementsQueryError = ErrorType<ProblemDetails>;

export function useGetUserProductEntitlements<
  TData = Awaited<ReturnType<typeof getUserProductEntitlements>>,
  TError = ErrorType<ProblemDetails>,
>(
  productId: string,
  params: undefined | GetUserProductEntitlementsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserProductEntitlements>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserProductEntitlements>>,
          TError,
          Awaited<ReturnType<typeof getUserProductEntitlements>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetUserProductEntitlements<
  TData = Awaited<ReturnType<typeof getUserProductEntitlements>>,
  TError = ErrorType<ProblemDetails>,
>(
  productId: string,
  params?: GetUserProductEntitlementsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserProductEntitlements>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getUserProductEntitlements>>,
          TError,
          Awaited<ReturnType<typeof getUserProductEntitlements>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetUserProductEntitlements<
  TData = Awaited<ReturnType<typeof getUserProductEntitlements>>,
  TError = ErrorType<ProblemDetails>,
>(
  productId: string,
  params?: GetUserProductEntitlementsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserProductEntitlements>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get user product entitlements
 */

export function useGetUserProductEntitlements<
  TData = Awaited<ReturnType<typeof getUserProductEntitlements>>,
  TError = ErrorType<ProblemDetails>,
>(
  productId: string,
  params?: GetUserProductEntitlementsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getUserProductEntitlements>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetUserProductEntitlementsQueryOptions(productId, params, options);

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}

/**
 * Gets the rolled-up entitlements for an organization's product registration. Returns detailed information including: effective count (sum of cumulative counts or max of minimum counts), trial status, expiration dates, and individual entitlement breakdown. For user authentication, requires Reader, Operator, or Administrator role in the organization. For service-to-service authentication, requires 'Entitlements.Read' role. Optionally include a signed JWT with entitlement claims by setting 'jwt=true'.
 * @summary Get organization product entitlements
 */
export const getOrganizationProductEntitlements = (
  orgId: string,
  productId: string,
  params?: GetOrganizationProductEntitlementsParams,
  options?: SecondParameter<typeof instance>,
  signal?: AbortSignal
) => {
  return instance<ProductEntitlementsRollup>(
    { url: `/v1/orgs/${orgId}/products/${productId}/entitlements`, method: 'GET', params, signal },
    options
  );
};

export const getGetOrganizationProductEntitlementsQueryKey = (
  orgId?: string,
  productId?: string,
  params?: GetOrganizationProductEntitlementsParams
) => {
  return [
    `/v1/orgs/${orgId}/products/${productId}/entitlements`,
    ...(params ? [params] : []),
  ] as const;
};

export const getGetOrganizationProductEntitlementsQueryOptions = <
  TData = Awaited<ReturnType<typeof getOrganizationProductEntitlements>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  params?: GetOrganizationProductEntitlementsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrganizationProductEntitlements>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  }
) => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey =
    queryOptions?.queryKey ??
    getGetOrganizationProductEntitlementsQueryKey(orgId, productId, params);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getOrganizationProductEntitlements>>> = ({
    signal,
  }) => getOrganizationProductEntitlements(orgId, productId, params, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!(orgId && productId), ...queryOptions } as UseQueryOptions<
    Awaited<ReturnType<typeof getOrganizationProductEntitlements>>,
    TError,
    TData
  > & { queryKey: DataTag<QueryKey, TData> };
};

export type GetOrganizationProductEntitlementsQueryResult = NonNullable<
  Awaited<ReturnType<typeof getOrganizationProductEntitlements>>
>;
export type GetOrganizationProductEntitlementsQueryError = ErrorType<ProblemDetails>;

export function useGetOrganizationProductEntitlements<
  TData = Awaited<ReturnType<typeof getOrganizationProductEntitlements>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  params: undefined | GetOrganizationProductEntitlementsParams,
  options: {
    query: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrganizationProductEntitlements>>, TError, TData>
    > &
      Pick<
        DefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganizationProductEntitlements>>,
          TError,
          Awaited<ReturnType<typeof getOrganizationProductEntitlements>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): DefinedUseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrganizationProductEntitlements<
  TData = Awaited<ReturnType<typeof getOrganizationProductEntitlements>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  params?: GetOrganizationProductEntitlementsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrganizationProductEntitlements>>, TError, TData>
    > &
      Pick<
        UndefinedInitialDataOptions<
          Awaited<ReturnType<typeof getOrganizationProductEntitlements>>,
          TError,
          Awaited<ReturnType<typeof getOrganizationProductEntitlements>>
        >,
        'initialData'
      >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
export function useGetOrganizationProductEntitlements<
  TData = Awaited<ReturnType<typeof getOrganizationProductEntitlements>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  params?: GetOrganizationProductEntitlementsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrganizationProductEntitlements>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> };
/**
 * @summary Get organization product entitlements
 */

export function useGetOrganizationProductEntitlements<
  TData = Awaited<ReturnType<typeof getOrganizationProductEntitlements>>,
  TError = ErrorType<ProblemDetails>,
>(
  orgId: string,
  productId: string,
  params?: GetOrganizationProductEntitlementsParams,
  options?: {
    query?: Partial<
      UseQueryOptions<Awaited<ReturnType<typeof getOrganizationProductEntitlements>>, TError, TData>
    >;
    request?: SecondParameter<typeof instance>;
  },
  queryClient?: QueryClient
): UseQueryResult<TData, TError> & { queryKey: DataTag<QueryKey, TData> } {
  const queryOptions = getGetOrganizationProductEntitlementsQueryOptions(
    orgId,
    productId,
    params,
    options
  );

  const query = useQuery(queryOptions, queryClient) as UseQueryResult<TData, TError> & {
    queryKey: DataTag<QueryKey, TData>;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
}
